import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import {
  Alert,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadApiFile } from '../../api/download';
import {
  acknowledgeInbound,
  checkInboundWaiting,
  downloadInbound,
  listInboundPending,
  listReceivedDocuments,
} from '../../api/endpoints';
import { gateOffsetForNewLimit } from '../../api/paging';
import { queryKeys } from '../../api/queryKeys';
import {
  flowisDefaultLimit,
  flowisReceivedStatuses,
  type FlowisDocumentQuery,
  type InboundDocument,
} from '../../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { gateStatusLabel } from '../../components/statusLabels';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { useSelectedTenant } from '../../tenant/tenantSelection';
import { formatAmount, formatDate } from '../../utils/format';
import { FlowisDocumentDialog } from './FlowisDocumentDialog';
import { FlowisDocumentsTable } from './FlowisDocumentsTable';

function useDownloadToBridge(accountGid: string, participantId: string | null) {
  const queryClient = useQueryClient();
  const { selectedTenantGid } = useSelectedTenant();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (flowisDocumentId: string) =>
      downloadInbound(participantId ?? '', flowisDocumentId, { includePdf: true, markDownloaded: true }),
    onSuccess: async (result) => {
      // The document now exists as stored files, is no longer pending, and the gate's own list has
      // it flagged as consumed — all three views are stale.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.files.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.inbound.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.flowis.account(selectedTenantGid, accountGid) }),
      ]);
      toastSuccess(
        result.alreadyDownloaded ? t('flowis.received.alreadyPulled') : t('flowis.received.pulled'),
      );
    },
    onError: toastError,
  });
}

/** The files one collected document produced, each fetched by its own identifier. */
function DocumentFiles({ document }: { document: InboundDocument }) {
  const { t } = useTranslation();

  const files: Array<[string, string | null]> = [
    ['xml', document.xmlFileGid],
    ['pdf', document.pdfFileGid],
    ['json', document.jsonFileGid],
    ...document.attachmentFileGids.map(
      (gid, index) => [`${t('flowis.received.attachmentShort')} ${index + 1}`, gid] as [string, string],
    ),
  ];

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {files
        .filter(([, gid]) => gid !== null)
        .map(([label, gid]) => (
          <Chip
            key={gid}
            size="small"
            variant="outlined"
            label={label}
            onClick={() => void downloadApiFile(`/file/${gid}`)}
          />
        ))}
    </Stack>
  );
}

/**
 * The documents this service has collected and nobody has confirmed taking. Read from our own
 * records rather than from the gate: what matters here is whether the receiving system has the
 * document, which the gate knows nothing about.
 */
function InboundQueueTable({ accountGid, participantId }: { accountGid: string; participantId: string | null }) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();

  const pending = useQuery({
    queryKey: queryKeys.inbound.pending(selectedTenantGid, accountGid),
    queryFn: ({ signal }) => listInboundPending(participantId ?? '', signal),
    enabled: !!participantId,
  });

  // Answers 'has anything been missed', which the queue itself cannot: a document the sweep has not
  // collected yet is not in it.
  const waiting = useQuery({
    queryKey: queryKeys.inbound.waiting(selectedTenantGid, accountGid),
    queryFn: ({ signal }) => checkInboundWaiting(participantId ?? '', signal),
    enabled: !!participantId,
  });

  const acknowledge = useMutation({
    mutationFn: (receivedDocumentGid: string) => acknowledgeInbound(participantId ?? '', receivedDocumentGid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.inbound.all });
      toastSuccess(t('flowis.received.acknowledged'));
    },
    onError: toastError,
  });

  if (!participantId) return <EmptyBlock message={t('flowis.received.noParticipant')} />;
  if (pending.isPending) return <LoadingBlock label={t('flowis.received.askingPending')} />;
  if (pending.isError) return <ErrorBlock error={pending.error} onRetry={() => void pending.refetch()} />;

  const items = pending.data ?? [];
  const stillAtGate = waiting.data?.pendingCount ?? 0;

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {t('flowis.received.queueCount', { count: items.length })}
        {stillAtGate > 0 ? ` · ${t('flowis.received.waitingAtGate', { count: stillAtGate })}` : ''}
      </Typography>

      {items.length === 0 ? (
        <EmptyBlock message={t('flowis.received.queueEmpty')} />
      ) : (
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('flowis.columns.invoiceNo')}</TableCell>
                  <TableCell>{t('flowis.columns.type')}</TableCell>
                  <TableCell>{t('flowis.columns.issued')}</TableCell>
                  <TableCell>{t('flowis.columns.seller')}</TableCell>
                  <TableCell align="right">{t('flowis.columns.totalInclVat')}</TableCell>
                  <TableCell>{t('flowis.received.filesColumn')}</TableCell>
                  <TableCell align="right">{t('columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.receivedDocumentGid}>
                    <TableCell>{item.invoiceId}</TableCell>
                    <TableCell>{item.documentType}</TableCell>
                    <TableCell>{formatDate(item.issueDate)}</TableCell>
                    <TableCell>{item.sellerName}</TableCell>
                    <TableCell align="right">
                      {item.totalVatIncluded === null
                        ? '—'
                        : formatAmount(item.totalVatIncluded, item.currencyCode)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <DocumentFiles document={item} />
                        {item.parseError ? (
                          <Tooltip title={item.parseError}>
                            <Chip size="small" color="warning" label={t('flowis.received.unreadable')} />
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<DoneAllIcon />}
                        disabled={acknowledge.isPending}
                        onClick={() => acknowledge.mutate(item.receivedDocumentGid)}
                      >
                        {t('flowis.received.acknowledge')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Stack>
  );
}

export function ReceivedTab({
  accountGid,
  participantId,
}: {
  accountGid: string;
  participantId: string | null;
}) {
  const download = useDownloadToBridge(accountGid, participantId);
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(flowisDefaultLimit);
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [downloadedFilter, setDownloadedFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const query = useMemo<FlowisDocumentQuery>(
    () => ({
      offset,
      limit,
      ...(status ? { status } : {}),
      // The gate takes plain dates, not instants.
      ...(fromDate?.isValid() ? { fromDate: fromDate.format('YYYY-MM-DD') } : {}),
      ...(toDate?.isValid() ? { toDate: toDate.format('YYYY-MM-DD') } : {}),
      ...(downloadedFilter === 'all' ? {} : { downloaded: downloadedFilter === 'yes' }),
    }),
    [offset, limit, status, fromDate, toDate, downloadedFilter],
  );

  const documents = useQuery({
    queryKey: queryKeys.flowis.received(selectedTenantGid, accountGid, query),
    queryFn: ({ signal }) => listReceivedDocuments(accountGid, query, signal),
    enabled: accountGid.length > 0 && !pendingOnly,
  });

  return (
    <Stack spacing={2}>
      <Alert severity="info">{t('flowis.received.note')}</Alert>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <FormControlLabel
            control={
              <Switch checked={pendingOnly} onChange={(event) => setPendingOnly(event.target.checked)} />
            }
            label={t('flowis.filters.pendingOnly')}
          />
          <TextField
            select
            size="small"
            label={t('columns.status')}
            value={status}
            disabled={pendingOnly}
            onChange={(event) => {
              setStatus(event.target.value);
              setOffset(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('flowis.filters.anyStatus')}</MenuItem>
            {flowisReceivedStatuses.map((value) => (
              <MenuItem key={value} value={value}>
                {gateStatusLabel(value, t)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('flowis.filters.consumed')}
            value={downloadedFilter}
            disabled={pendingOnly}
            onChange={(event) => {
              setDownloadedFilter(event.target.value as 'all' | 'yes' | 'no');
              setOffset(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">{t('flowis.filters.consumedAny')}</MenuItem>
            <MenuItem value="yes">{t('flowis.filters.consumedYes')}</MenuItem>
            <MenuItem value="no">{t('flowis.filters.consumedNo')}</MenuItem>
          </TextField>
          <DatePicker
            label={t('flowis.filters.issuedFrom')}
            value={fromDate}
            disabled={pendingOnly}
            onChange={(value) => {
              setFromDate(value);
              setOffset(0);
            }}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />
          <DatePicker
            label={t('flowis.filters.issuedTo')}
            value={toDate}
            disabled={pendingOnly}
            onChange={(value) => {
              setToDate(value);
              setOffset(0);
            }}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />
        </Stack>
      </Paper>

      {pendingOnly ? (
        <InboundQueueTable accountGid={accountGid} participantId={participantId} />
      ) : (
        <FlowisDocumentsTable
          page={documents.data}
          isPending={documents.isPending}
          error={documents.error}
          onRetry={() => void documents.refetch()}
          onSelect={setSelected}
          offset={offset}
          limit={limit}
          onOffsetChange={setOffset}
          onLimitChange={(next) => {
            setOffset(gateOffsetForNewLimit(offset, next));
            setLimit(next);
          }}
          actionHeader={t('flowis.received.bridgeColumn')}
          renderAction={(row) =>
            row.downloaded ? (
              <Chip size="small" label={t('flowis.received.consumedChip')} variant="outlined" />
            ) : (
              <Tooltip
                title={
                  participantId
                    ? t('flowis.received.downloadTooltip')
                    : t('flowis.received.noParticipant')
                }
              >
                <span>
                  <Button
                    size="small"
                    startIcon={<CloudDownloadIcon />}
                    disabled={download.isPending || !participantId || row.id === null}
                    onClick={() => {
                      if (row.id !== null) download.mutate(row.id);
                    }}
                  >
                    {t('actions.download')}
                  </Button>
                </span>
              </Tooltip>
            )
          }
        />
      )}

      <FlowisDocumentDialog
        accountGid={accountGid}
        direction="received"
        documentId={selected}
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}
